import { hashPassword } from "../../constants/encryptPassword";
import {
  buildCredentialsEmailHtml,
  credentialsEmailSubject,
} from "../email/credentialsEmailTemplate";
import {
  buildWorkflowEmailHtml,
  workflowEmailSubject,
  type WorkflowEmailStage,
} from "../email/workflowEmailTemplate";
import {
  buildImportProgressPayload,
  ExcelImportProgress,
  parseImportProgress,
} from "../../excel/import/progress";
import { getProductID, getProductType } from "../../constants/login";
import {
  clearOnboardingToken,
  resolveOnboardingJwtForAuth,
} from "../../constants/onboardingToken";
import {
  normalizeImportDataUtilityItems,
  resolveImportApiFileName,
  resolveImportModuleName,
} from "../../excel/import/moduleNames";
import { assertOnboardingImportExcelDataBody } from "../../excel/import/importExcelDataRequest";
import { interpretImportExcelDataResponse } from "../../excel/import/interpretImportExcelDataResponse";
import { DataUtilityImportItem } from "../actions/ImportExcelDataMapper";

/** Dev server uses setupProxy with relative URLs; deployed builds call the API host directly. */
const ENDPOINT_URL =
  process.env.NODE_ENV === "production"
    ? (process.env.REACT_APP_SERVICE_URL || "").replace(/\/$/, "")
    : "";

const REPLY_TO_EMAIL =
  process.env.REACT_APP_REPLY_TO_EMAIL || "PMSOnboardingTeam@agilysys.com";
const FROM_EMAIL =
  process.env.REACT_APP_FROM_EMAIL || "onboarding@agilysys.com";
const ONBOARDING_EMAIL =
  process.env.REACT_APP_ONBOARDING_EMAIL || "PMSOnboardingTeam@agilysys.com";
const ONBOARDING_URL =
  process.env.REACT_APP_ONBOARDING_URL ||
  "https://versa-onboardui-dev.onagilysys.com/";

export const loginService = async (
  username: string,
  password: string,
  TENANT_ID: number,
  PROPERTY_ID: number,
  PRODUCT_ID: string,
) => {
  try {
    const productId = getProductID(PRODUCT_ID);
    const response = await fetch(`${ENDPOINT_URL}/OnBoarding/login`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        username,
        password: await hashPassword(password),
        tenantId: TENANT_ID,
        propertyId: PROPERTY_ID,
        productId,
      }),
    });

    // if (!response.ok) {
    //   throw new Error(`HTTP error! Status: ${response.status}`);
    // }

    return response;
  } catch (error) {
    return error;
  }
};

export const createClientInformation = async (
  data: any,
  TENANT_ID: number,
  PROPERTY_ID: number,
  PRODUCT_ID: number,
) => {
  try {
    const response = await fetch(`${ENDPOINT_URL}/OnBoarding/CreateSettings`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        tenantId: TENANT_ID,
        propertyId: PROPERTY_ID,
        productId: PRODUCT_ID,
        configValue: JSON.stringify(data),
      }),
    });
    return response;
  } catch (error) {
    return error;
  }
};

/** Optional excel-import resume endpoint; 404 means no progress (safe to ignore). */
const importProgressBaseUrl = () =>
  process.env.REACT_APP_IMPORT_PROGRESS_URL ||
  `${ENDPOINT_URL}/OnBoarding/ImportProgress`;

export const getImportProgress = async (
  TENANT_ID: number,
  PROPERTY_ID: number,
  PRODUCT_ID: number,
) => {
  try {
    const response = await fetch(
      `${importProgressBaseUrl()}/${TENANT_ID}/${PROPERTY_ID}/${PRODUCT_ID}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );
    if (response.status === 404) {
      return null;
    }
    if (!response.ok) {
      return null;
    }
    const json = await response.json();
    return parseImportProgress(json);
  } catch {
    return null;
  }
};

export const saveImportProgress = async (
  TENANT_ID: number,
  PROPERTY_ID: number,
  PRODUCT_ID: number,
  progress: ExcelImportProgress,
) => {
  return fetch(importProgressBaseUrl(), {
    method: "PUT",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(
      buildImportProgressPayload(TENANT_ID, PROPERTY_ID, PRODUCT_ID, progress),
    ),
  });
};

export const resetImportProgress = async (
  TENANT_ID: number,
  PROPERTY_ID: number,
  PRODUCT_ID: number,
) => {
  return fetch(
    `${importProgressBaseUrl()}/${TENANT_ID}/${PROPERTY_ID}/${PRODUCT_ID}`,
    {
      method: "DELETE",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    },
  );
};

export const updateUserToken = async (
  TENANT_ID: number,
  PROPERTY_ID: number,
  PRODUCT_ID: number,
): Promise<Response | Error> => {
  try {
    const response = await fetch(`${ENDPOINT_URL}/OnBoarding/updatetoken`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        tenantId: TENANT_ID,
        propertyId: PROPERTY_ID,
        productId: PRODUCT_ID,
      }),
    });
    return response;
  } catch (error) {
    return error instanceof Error ? error : new Error(String(error));
  }
};

export const checkAuthStatus = async () => {
  try {
    const response = await fetch(`${ENDPOINT_URL}/OnBoarding/authstatus`, {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });
    return response.json();
  } catch (error) {
    return error;
  }
};

export const signoutUser = async () => {
  try {
    clearOnboardingToken();
    const response = await fetch(`${ENDPOINT_URL}/OnBoarding/signout`, {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });
    return response;
  } catch (error) {
    return error;
  }
};

export const getClientInformation = async (
  TENANT_ID: number,
  PROPERTY_ID: number,
  PRODUCT_ID: number,
) => {
  try {
    const response = await fetch(
      `${ENDPOINT_URL}/OnBoarding/GetSettings/${TENANT_ID}/${PROPERTY_ID}/${PRODUCT_ID}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    return response;
  } catch (error) {
    return error;
  }
};

export const updateClientInformation = async (
  data: any,
  TENANT_ID: number,
  PROPERTY_ID: number,
  PRODUCT_ID: number,
) => {
  try {
    const response = await fetch(`${ENDPOINT_URL}/OnBoarding/UpdateSettings`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        tenantId: TENANT_ID,
        propertyId: PROPERTY_ID,
        productId: PRODUCT_ID,
        configValue: JSON.stringify(data),
      }),
    });

    const responseData = await response.json();
    if (response.status === 401) {
      await signoutUser();
      window.location.reload();
    }
    return responseData;
  } catch (error) {
    return error;
  }
};

export const deleteClientInformation = async (
  TENANT_ID: number,
  PROPERTY_ID: number,
  PRODUCT_ID: number,
) => {
  try {
    const response = await fetch(`${ENDPOINT_URL}/OnBoarding/DeleteSettings`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        settings: {
          tenantId: TENANT_ID,
          propertyId: PROPERTY_ID,
          productId: PRODUCT_ID,
        },
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    return response;
  } catch (error) {
    return error;
  }
};

export type SendCredentialsEmailParams = {
  toAddress: string;
  firstName: string;
  lastName: string;
  username: string;
  password: string;
  customerName: string;
  productId: number;
  tenantId?: number;
  propertyId?: number;
};

export const sendCredentialsEmail = async (
  params: SendCredentialsEmailParams,
) => {
  const messageBody = buildCredentialsEmailHtml({
    firstName: params.firstName,
    lastName: params.lastName,
    username: params.username,
    password: params.password,
    customerName: params.customerName,
    onboardingUrl: ONBOARDING_URL,
    productId: params.productId,
    tenantId: params.tenantId,
    propertyId: params.propertyId,
  });

  try {
    const response = await fetch(`${ENDPOINT_URL}/sendemail`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        emailDeliveryRequest: {
          content: {
            type: "HTML",
            objectType: "formattedContent",
            messageBody,
          },
          metadata: {
            locale: "en_US",
            fromAddress: FROM_EMAIL,
            subject: credentialsEmailSubject(params.customerName),
            toAddresses: [params.toAddress],
            replyToAddress: REPLY_TO_EMAIL,
            fromName: "Agilysys Onboarding",
          },
        },
      }),
    });
    const responseData = await response.json();
    return { response, data: responseData };
  } catch (error) {
    return { response: null, data: error };
  }
};

export const sendWorkflowSubmitEmail = async (params: {
  stage: WorkflowEmailStage;
  toAddresses: string[];
  productId: number;
  submitterFirstName: string;
  submitterLastName: string;
  customerName: string;
}) => {
  const productName = getProductType(params.productId) ?? "Onboarding";
  const emailData = {
    stage: params.stage,
    submitterFirstName: params.submitterFirstName,
    submitterLastName: params.submitterLastName,
    customerName: params.customerName,
    productId: params.productId,
    productName,
    onboardingUrl: ONBOARDING_URL,
  };
  const messageBody = buildWorkflowEmailHtml(emailData);

  try {
    const response = await fetch(`${ENDPOINT_URL}/sendemail`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        emailDeliveryRequest: {
          content: {
            type: "HTML",
            objectType: "formattedContent",
            messageBody,
          },
          metadata: {
            locale: "en_US",
            fromAddress: FROM_EMAIL,
            subject: workflowEmailSubject(emailData),
            toAddresses: params.toAddresses,
            replyToAddress: REPLY_TO_EMAIL,
            fromName: `Agilysys Onboarding - ${productName.toUpperCase()}`,
          },
        },
      }),
    });
    const responseData = await response.json();
    return { response, data: responseData };
  } catch (error) {
    return { response: null, data: error };
  }
};

/** @deprecated Use sendWorkflowSubmitEmail with stage-specific recipients. */
export const sendEmailNotification = async (
  toAddresses: string[],
  productId: number,
  firstName: string,
  lastName: string,
  customer: string,
) => {
  return sendWorkflowSubmitEmail({
    stage: "customer_submitted",
    toAddresses,
    productId,
    submitterFirstName: firstName,
    submitterLastName: lastName,
    customerName: customer,
  });
};

export const getUserEmail = async () => {
  try {
    const response = await fetch(`${ENDPOINT_URL}/OnBoarding/username`, {
      method: "GET",
      credentials: "include",
    });
    const responseData = await response.json();
    return responseData;
  } catch (error) {
    return error;
  }
};

export type OnboardingTenant = {
  tenantId: number;
  propertyId: number;
  productId: number;
};

export type OnboardingCustomer = {
  id?: number;
  code: string;
  name: string;
  email: string;
  implementationTeamEmails: string[];
  technicalProjectManagerEmail: string;
  projectManagerEmail: string;
};

export type CreateOnboardingUserCustomerPayload = {
  code: string;
  name: string;
  email: string;
  implementationTeamEmails: string[];
  technicalProjectManagerEmail: string;
  projectManagerEmail: string;
};

export type OnboardingUserRole = {
  id: number;
  code: string;
  name: string;
  sortOrder: number;
};

export type CreateOnboardingUserInput = {
  user: {
    username: string;
    password: string;
    email: string;
    firstName: string;
    lastName: string;
    userRoleCode?: string;
    userRoleId?: number;
  };
  tenantId: number;
  propertyId: number;
  productIds: number[];
  customer: CreateOnboardingUserCustomerPayload;
};

export type OnboardingUser = {
  id: number;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  userRoleCode: string;
  userRole?: OnboardingUserRole;
  /** @deprecated Legacy numeric role from older API responses */
  role?: number;
  active?: boolean;
  customer: OnboardingCustomer;
  tenants: OnboardingTenant[];
};

export type UpdateOnboardingUserBody = {
  firstName?: string;
  lastName?: string;
  email?: string;
  userRoleCode?: string;
  userRoleId?: number;
  tenantId?: number;
  propertyId?: number;
  productIds?: number[];
  customer?: CreateOnboardingUserCustomerPayload;
  password?: string;
};

export type OnboardingSettingsResponse = {
  configValue: string | null;
  userRole?: OnboardingUserRole | null;
  status?: string;
  readOnly?: boolean;
  /** @deprecated Legacy role string from older settings responses */
  role?: string;
};

export type ParsedOnboardingSettings = {
  configValue: Record<string, unknown> | null;
  userRole: OnboardingUserRole | null;
  userRoleCode: string;
  status: string;
  /** Present only when the API explicitly returns readOnly. */
  readOnly?: boolean;
};

export const parseCustomerIds = (json: unknown): string[] =>
  parseCustomers(json).map((c) => customerRouteKey(c));

/** Identifier used in GET …/customers/{id}/users (customer code). */
export const customerRouteKey = (c: OnboardingCustomer): string =>
  c.code.trim() || c.name.trim();

/** Parses implementation team emails from API field variants. */
export const parseImplementationTeamEmailsFromRecord = (
  o: Record<string, unknown>,
): string[] => {
  const teamRaw = o.implementationTeamEmails;
  if (Array.isArray(teamRaw)) {
    return teamRaw.map((e) => String(e).trim()).filter(Boolean);
  }
  const legacyRaw = o.implementationEmail;
  if (legacyRaw != null && String(legacyRaw).trim()) {
    const legacy = String(legacyRaw).trim();
    if (legacy.startsWith("[")) {
      try {
        const parsed: unknown = JSON.parse(legacy);
        if (Array.isArray(parsed)) {
          return parsed.map((e) => String(e).trim()).filter(Boolean);
        }
      } catch {
        /* fall through */
      }
    }
    return legacy
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  }
  return [];
};

export const parseCustomerRecord = (
  item: unknown,
): OnboardingCustomer | null => {
  if (typeof item === "string") {
    const name = item.trim();
    if (!name) return null;
    return {
      code: name,
      name,
      email: "",
      implementationTeamEmails: [],
      technicalProjectManagerEmail: "",
      projectManagerEmail: "",
    };
  }
  if (!item || typeof item !== "object") return null;
  const o = item as Record<string, unknown>;
  const code = String(o.code ?? "").trim();
  const name = String(o.name ?? "").trim();
  if (!code && !name) return null;
  const email = String(
    o.email ?? o.contactEmail ?? o.customerEmail ?? "",
  ).trim();
  const technicalProjectManagerEmail = String(
    o.technicalProjectManagerEmail ?? o.tpmEmail ?? o.technicalPmEmail ?? "",
  ).trim();
  const projectManagerEmail = String(
    o.projectManagerEmail ?? o.pmEmail ?? "",
  ).trim();
  return {
    id: typeof o.id === "number" ? o.id : undefined,
    code: code || name,
    name: name || code,
    email,
    implementationTeamEmails: parseImplementationTeamEmailsFromRecord(o),
    technicalProjectManagerEmail,
    projectManagerEmail,
  };
};

export const parseCustomers = (json: unknown): OnboardingCustomer[] => {
  if (!Array.isArray(json)) return [];
  return json
    .map((item) => parseCustomerRecord(item))
    .filter((c): c is OnboardingCustomer => c !== null);
};

export const implementationTeamEmailsToFormValue = (
  emails: string[] | undefined,
): string =>
  (emails ?? [])
    .map((e) => e.trim())
    .filter(Boolean)
    .join(", ");

export const formValueToImplementationTeamEmails = (form: string): string[] =>
  form
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

export const customerToCreatePayload = (
  c: OnboardingCustomer,
): CreateOnboardingUserCustomerPayload => ({
  code: c.code,
  name: c.name,
  email: c.email,
  implementationTeamEmails: c.implementationTeamEmails,
  technicalProjectManagerEmail: c.technicalProjectManagerEmail,
  projectManagerEmail: c.projectManagerEmail,
});

/**
 * API returns `implementationEmail` as either a plain address or a
 * JSON-serialized string[] (e.g. `["a@x.com","b@x.com"]`).
 * Produces comma-separated text for the form.
 */
export const implementationEmailToFormValue = (
  raw: string | undefined | null,
): string => {
  if (raw == null) return "";
  const trimmed = String(raw).trim();
  if (!trimmed) return "";
  if (trimmed.startsWith("[")) {
    try {
      const parsed: unknown = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed
          .map((e) => String(e).trim())
          .filter(Boolean)
          .join(", ");
      }
    } catch {
      /* leave as raw */
    }
  }
  return trimmed;
};

/**
 * Converts comma-separated addresses from the form into the JSON-stringified
 * array string shape returned by GET …/customers/…/users.
 */
export const formValueToImplementationEmailApi = (form: string): string => {
  const parts = form
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  if (parts.length === 0) return "";
  return JSON.stringify(parts);
};

export const getCustomers = async (): Promise<Response> => {
  return fetch(`${ENDPOINT_URL}/OnBoarding/customers`, {
    method: "GET",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
  });
};

export const getUsersForCustomer = async (
  customerId: string,
): Promise<Response> => {
  const encoded = encodeURIComponent(customerId);
  return fetch(`${ENDPOINT_URL}/OnBoarding/customers/${encoded}/users`, {
    method: "GET",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
  });
};

export const getOnboardingUsers = async (): Promise<Response> => {
  return fetch(`${ENDPOINT_URL}/OnBoarding/users`, {
    method: "GET",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
  });
};

export const parseUserRole = (json: unknown): OnboardingUserRole | null => {
  if (!json || typeof json !== "object") return null;
  const o = json as Record<string, unknown>;
  const id = typeof o.id === "number" ? o.id : Number(o.id);
  const code = String(o.code ?? o.userRoleCode ?? "").trim();
  const name = String(o.name ?? "").trim();
  const sortOrder =
    typeof o.sortOrder === "number" ? o.sortOrder : Number(o.sortOrder);
  if (!code) return null;
  return {
    id: Number.isFinite(id) && id > 0 ? id : 0,
    code,
    name: name || code,
    sortOrder: Number.isFinite(sortOrder) ? sortOrder : 0,
  };
};

export const normalizeRoleCodeToCatalog = (
  code: string,
  catalog: OnboardingUserRole[],
): string => {
  const normalized = code.trim().toLowerCase();
  if (!normalized) return code;
  const match = catalog.find((r) => r.code.trim().toLowerCase() === normalized);
  return match?.code ?? code;
};

export const parseUserRoles = (json: unknown): OnboardingUserRole[] => {
  if (!Array.isArray(json)) return [];
  return json
    .map((item) => parseUserRole(item))
    .filter((r): r is OnboardingUserRole => r !== null)
    .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
};

const resolveUserRoleCodeFromRecord = (
  o: Record<string, unknown>,
  roleById?: Map<number, string>,
): string => {
  const nested = parseUserRole(o.userRole);
  if (nested?.code) return nested.code;
  if (typeof o.userRoleCode === "string" && o.userRoleCode.trim()) {
    return o.userRoleCode.trim();
  }
  const userRoleId =
    typeof o.userRoleId === "number" ? o.userRoleId : Number(o.userRoleId);
  if (
    roleById &&
    Number.isFinite(userRoleId) &&
    userRoleId > 0 &&
    roleById.has(userRoleId)
  ) {
    return roleById.get(userRoleId)!;
  }
  if (typeof o.roleCode === "string" && o.roleCode.trim()) {
    return o.roleCode.trim();
  }
  if (typeof o.role === "number") {
    if (o.role === 2) return "admin";
    if (o.role === 1) return "implementationTeam";
    return "customer";
  }
  if (typeof o.role === "string" && o.role.trim()) {
    const r = o.role.trim().toLowerCase();
    if (r === "agilysys") return "implementationTeam";
    return o.role.trim();
  }
  return "customer";
};

export const parseOnboardingSettings = (
  json: unknown,
): ParsedOnboardingSettings => {
  const empty: ParsedOnboardingSettings = {
    configValue: null,
    userRole: null,
    userRoleCode: "customer",
    status: "draft",
  };
  if (!json || typeof json !== "object") return empty;
  const o = json as Record<string, unknown>;
  const userRole = parseUserRole(o.userRole);
  let configValue: Record<string, unknown> | null = null;
  // Backend may return configValue as a JSON string or an already-parsed object.
  // Settings are shared by (tenantId, propertyId, productId) — not per user.
  if (typeof o.configValue === "string" && o.configValue.trim()) {
    try {
      const parsed = JSON.parse(o.configValue);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        configValue = parsed as Record<string, unknown>;
      }
    } catch {
      configValue = null;
    }
  } else if (
    o.configValue &&
    typeof o.configValue === "object" &&
    !Array.isArray(o.configValue)
  ) {
    configValue = o.configValue as Record<string, unknown>;
  }
  const userRoleCode = userRole?.code ?? resolveUserRoleCodeFromRecord(o);
  return {
    configValue,
    userRole,
    userRoleCode,
    status: typeof o.status === "string" ? o.status : "draft",
    ...(typeof o.readOnly === "boolean" ? { readOnly: o.readOnly } : {}),
  };
};

export const getUserRoles = async (): Promise<Response> => {
  return fetch(`${ENDPOINT_URL}/OnBoarding/userroles`, {
    method: "GET",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
  });
};

export const createUserRole = async (body: {
  code: string;
  name: string;
  sortOrder: number;
}): Promise<Response> => {
  return fetch(`${ENDPOINT_URL}/OnBoarding/userroles`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
};

export const parseOnboardingUsers = (
  json: unknown,
  roleCatalog?: OnboardingUserRole[],
): OnboardingUser[] => {
  const roleById = roleCatalog
    ? new Map(roleCatalog.map((r) => [r.id, r.code]))
    : undefined;
  if (!Array.isArray(json)) return [];
  return json
    .map((item): OnboardingUser | null => {
      if (!item || typeof item !== "object") return null;
      const o = item as Record<string, unknown>;
      const id = typeof o.id === "number" ? o.id : Number(o.id);
      if (!id || Number.isNaN(id)) return null;
      const customerParsed = parseCustomerRecord(o.customer);
      if (!customerParsed) return null;
      const tenantsRaw = o.tenants;
      const tenants: OnboardingTenant[] = Array.isArray(tenantsRaw)
        ? tenantsRaw
            .map((t): OnboardingTenant | null => {
              if (!t || typeof t !== "object") return null;
              const row = t as Record<string, unknown>;
              const tenantId = Number(row.tenantId);
              const propertyId = Number(row.propertyId);
              const productId = Number(row.productId);
              if (
                !Number.isFinite(tenantId) ||
                !Number.isFinite(propertyId) ||
                !Number.isFinite(productId)
              ) {
                return null;
              }
              return { tenantId, propertyId, productId };
            })
            .filter((t): t is OnboardingTenant => t !== null)
        : [];
      const userRole = parseUserRole(o.userRole);
      const userRoleCode = resolveUserRoleCodeFromRecord(o, roleById);
      return {
        id,
        username: String(o.username ?? "").trim(),
        email: String(o.email ?? "").trim(),
        firstName: String(o.firstName ?? "").trim(),
        lastName: String(o.lastName ?? "").trim(),
        userRoleCode,
        userRole: userRole ?? undefined,
        role: typeof o.role === "number" ? o.role : undefined,
        active: typeof o.active === "boolean" ? o.active : undefined,
        customer: customerParsed,
        tenants,
      };
    })
    .filter((u): u is OnboardingUser => u !== null);
};

/**
 * POST /OnBoarding/ImportExcelData — onboarding JWT in `token` header when readable
 * (+ cookie via credentials). For local .NET, copy setupProxy.example.js over
 * setupProxy.js so the proxy can inject HttpOnly jwtToken into the token header.
 * Call once per sheet (dataUtilityImportData length must be 1).
 *
 * Resolves on full success. Throws on hard failure.
 * Returns `{ partial: true, message }` when Visual One reports duplicates / partial import
 * (often HTTP 200 with result/successStatus false).
 */
export const importExcelData = async (
  tenantId: number,
  propertyId: number,
  productId: number,
  fileName: string,
  dataUtilityImportData: DataUtilityImportItem[],
  base64ExportFileData: string,
  options?: { moduleName?: string },
): Promise<{
  partial: boolean;
  message?: string;
  totalRows?: number;
  completedRows?: number;
}> => {
  const onboardingJwt = await resolveOnboardingJwtForAuth(
    () =>
      fetch(`${ENDPOINT_URL}/OnBoarding/authstatus`, {
        method: "GET",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      }),
    () => updateUserToken(tenantId, propertyId, productId),
  );

  const moduleName =
    options?.moduleName ?? resolveImportModuleName(productId, fileName);
  const apiFileName = resolveImportApiFileName(fileName, moduleName);
  const normalizedImportData = normalizeImportDataUtilityItems(
    dataUtilityImportData,
    moduleName,
  );

  const importBody = {
    tenantId,
    propertyId,
    productId,
    fileName: apiFileName,
    moduleName,
    dataUtilityImportData: normalizedImportData,
    base64ExportFileData,
  };

  assertOnboardingImportExcelDataBody(importBody);

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "application/json",
  };
  // Prefer explicit token header. If JWT is HttpOnly (local .NET), JS cannot read it —
  // credentials:include still sends the cookie; local setupProxy.example.js
  // can inject token for ImportExcelData when testing against local .NET.
  if (onboardingJwt) {
    headers.token = onboardingJwt;
  }

  const response = await fetch(`${ENDPOINT_URL}/OnBoarding/ImportExcelData`, {
    method: "POST",
    credentials: "include",
    headers,
    body: JSON.stringify(importBody),
  });

  const outcome = await interpretImportExcelDataResponse(response);
  if (outcome.status === "failed") {
    throw new Error(outcome.message);
  }
  if (outcome.status === "partial") {
    return {
      partial: true,
      message: outcome.message,
      totalRows: outcome.totalRows,
      completedRows: outcome.completedRows,
    };
  }
  return {
    partial: false,
    totalRows: outcome.totalRows,
    completedRows: outcome.completedRows,
  };
};

/**
 * Creates or updates the customer and user in one request.
 * Single product → POST /OnBoarding/CreateUser (nested tenant).
 * Multiple products → POST /OnBoarding/CreateUserForProducts.
 */
export const createOnboardingUser = async (
  input: CreateOnboardingUserInput,
): Promise<Response> => {
  const { user, tenantId, propertyId, productIds, customer } = input;
  const userWithHashedPassword = {
    ...user,
    password: await hashPassword(user.password),
  };
  const headers = { "Content-Type": "application/json" };
  const init = { method: "POST", credentials: "include" as const, headers };

  if (productIds.length === 1) {
    return fetch(`${ENDPOINT_URL}/OnBoarding/CreateUser`, {
      ...init,
      body: JSON.stringify({
        user: userWithHashedPassword,
        customer,
        tenant: {
          tenantId,
          propertyId,
          productId: productIds[0],
        },
      }),
    });
  }

  return fetch(`${ENDPOINT_URL}/OnBoarding/CreateUserForProducts`, {
    ...init,
    body: JSON.stringify({
      user: userWithHashedPassword,
      customer,
      tenantId,
      propertyId,
      productIds,
    }),
  });
};

export const updateOnboardingUser = async (
  userId: number,
  body: UpdateOnboardingUserBody,
): Promise<Response> => {
  const payload: UpdateOnboardingUserBody = { ...body };
  if (payload.password) {
    payload.password = await hashPassword(payload.password);
  }
  return fetch(`${ENDPOINT_URL}/OnBoarding/users/${userId}`, {
    method: "PUT",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
};
